import type { ListBuilderConfiguration } from '../types';

/**
 * Vendor list configuration
 * Shows all vendors with contact information, location, and company details
 * Features template data config for address formatting
 * Converted from JSON to TypeScript for type safety
 */
export const VENDOR_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-vendors-all',
			cacheTTL: 600000,
			name: 'All Vendors',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'vendor-name',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'vendor',
								idField: '_id',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'externalId',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Name' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'vendor',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'location',
					fields: [
						{
							id: 'address1-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'address1',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-address',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address2-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'address2',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-address2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'city-state-zip',
							dataConfig: {
								type: 'template',
								template: '{vendor.city}, {vendor.state} {vendor.zip}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Location' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'state',
							objectTable: 'vendor',
						},
						{
							direction: 'ASC',
							fieldName: 'city',
							objectTable: 'vendor',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'contact',
					fields: [
						{
							id: 'phone-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'phone',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'phone',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'email-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'email',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'email',
						},
					],
					headers: [{ id: 'h3', text: 'Contact' }],
					minWidth: 200,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'company',
					fields: [
						{
							id: 'company-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'companyName',
									objectTable: 'vendor',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Company' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'companyName',
							objectTable: 'vendor',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
			],
			description:
				'Complete list of all vendors with contact and company information',
			defaultSortId: 'column-vendor-name-asc',
			requiredFields: [
				'vendor._id',
				'vendor.name',
				'vendor.externalId',
				'vendor.address1',
				'vendor.address2',
				'vendor.city',
				'vendor.state',
				'vendor.zip',
				'vendor.phone',
				'vendor.email',
				'vendor.companyName',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['vendorKeywords', 'vendorCompanies'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/vendors/:id',
			objectLabel: 'Vendor',
			objectTable: 'vendor',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'address1', fieldLabel: 'Address 1' },
				{ fieldName: 'address2', fieldLabel: 'Address 2' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'lastCallDate', fieldLabel: 'Last Call Date' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'vendor',
				},
			],
			description: 'Sort vendors by name',
		},
		{
			id: 'location',
			name: 'By Location',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'state',
					objectTable: 'vendor',
				},
				{
					direction: 'ASC',
					fieldName: 'city',
					objectTable: 'vendor',
				},
			],
			description: 'Sort by state then city',
		},
		{
			id: 'company',
			name: 'By Company',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'companyName',
					objectTable: 'vendor',
				},
			],
			description: 'Sort by company name',
		},
		{
			id: 'last-call-date-desc',
			name: 'Last Call Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastCallDate',
					objectTable: 'vendor',
				},
			],
			description: 'Sort by last call date (newest first)',
		},
		{
			id: 'last-call-date-asc',
			name: 'Last Call Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastCallDate',
					objectTable: 'vendor',
				},
			],
			description: 'Sort by last call date (oldest first)',
		},
	],
};
